import { useState, useMemo } from "react";
import { useQuery } from "react-query";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Landmark,
  Wallet,
  FileText,
  BookOpen,
  Clock,
  RefreshCw,
  AlertCircle,
  Loader2,
} from "lucide-react";
import { ColumnDef } from "@tanstack/react-table";
import { api } from "@/lib/api";
import { cn, formatCurrency } from "@/lib/utils";
import { PageTransition } from "@/components/PageTransition";
import DataTable from "@/components/datatable/Datatable";
import DataTableColumnHeader from "@/components/datatable/DataTableColumnHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

const formatMoney = (value: number | string | undefined | null) =>
  `${Number(value || 0).toLocaleString()} FRW`;

type TabKey = "overview" | "transactions" | "journals" | "ledger" | "audit";

const TABS: { key: TabKey; label: string; icon: React.ReactNode }[] = [
  { key: "overview", label: "Overview", icon: <FileText className="h-4 w-4" /> },
  { key: "transactions", label: "Transactions", icon: <Wallet className="h-4 w-4" /> },
  { key: "journals", label: "Journal Entries", icon: <BookOpen className="h-4 w-4" /> },
  { key: "ledger", label: "Ledger", icon: <Landmark className="h-4 w-4" /> },
  { key: "audit", label: "Audit Trail", icon: <Clock className="h-4 w-4" /> },
];

interface AccountDetail {
  id: number;
  accountCode: string;
  accountName: string;
  accountType: string;
  balance: number;
  status: string;
  description?: string;
  parentAccountId?: number | null;
  parentAccount?: { accountName: string; accountCode: string } | null;
  normalBalance?: string;
  isBankAccount?: boolean;
  bankName?: string;
  accountNumber?: string;
  createdAt: string;
  updatedAt: string;
}

interface Transaction {
  id: number;
  date: string;
  reference: string;
  sourceModule: string;
  debit: number;
  credit: number;
  runningBalance: number;
  user: string;
  description: string;
}

interface JournalEntry {
  id: number;
  journalNumber: string;
  entryDate: string;
  sourceModule: string;
  narration: string;
  debit: number;
  credit: number;
  accountCode: string;
  accountName: string;
  createdBy: string;
}

interface LedgerEntry {
  id: number;
  date: string;
  journalNumber: string;
  narration: string;
  source: string;
  debit: number;
  credit: number;
  runningBalance: number;
}

export default function AccountDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabKey>("overview");

  const [txPageIndex, setTxPageIndex] = useState(0);
  const [txPageSize] = useState(10);

  const [ledgerPageIndex, setLedgerPageIndex] = useState(0);
  const [ledgerPageSize] = useState(10);

  const { data: account, isLoading: accountLoading, error: accountError } = useQuery<AccountDetail>(
    ["account", id],
    async () => {
      const { data } = await api.get(`/accounts/${id}`);
      return data.data || data;
    },
    { enabled: !!id, staleTime: 30000 }
  );

  const { data: txData, isLoading: txLoading, isFetching: txFetching } = useQuery(
    ["account-transactions", id, txPageIndex, txPageSize],
    async () => {
      const params = new URLSearchParams();
      params.set("page", String(txPageIndex + 1));
      params.set("page_size", String(txPageSize));
      const { data } = await api.get(`/accounts/${id}/transactions?${params.toString()}`);
      return data;
    },
    { enabled: !!id && activeTab === "transactions", keepPreviousData: true }
  );

  const { data: journalData, isLoading: journalLoading, isFetching: journalFetching } = useQuery(
    ["account-journals", id],
    async () => {
      const { data } = await api.get(`/journals?accountId=${id}`);
      return data;
    },
    { enabled: !!id && activeTab === "journals" }
  );

  const { data: ledgerData, isLoading: ledgerLoading, isFetching: ledgerFetching } = useQuery(
    ["account-ledger", id, ledgerPageIndex, ledgerPageSize],
    async () => {
      const params = new URLSearchParams();
      params.set("page", String(ledgerPageIndex + 1));
      params.set("page_size", String(ledgerPageSize));
      const { data } = await api.get(`/accounts/${id}/ledger?${params.toString()}`);
      return data;
    },
    { enabled: !!id && activeTab === "ledger", keepPreviousData: true }
  );

  const transactions: Transaction[] = txData?.results || txData?.data || [];
  const txPagination = txData?.pagination || { total: 0, totalPages: 1 };

  const journalEntries: JournalEntry[] = journalData?.results || journalData?.data || [];
  const journalPagination = journalData?.pagination || { total: 0, totalPages: 1 };

  const ledgerEntries: LedgerEntry[] = ledgerData?.results || ledgerData?.data || [];
  const ledgerPagination = ledgerData?.pagination || { total: 0, totalPages: 1 };

  const txColumns: ColumnDef<Transaction, any>[] = useMemo(
    () => [
      {
        accessorKey: "date",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Date" />,
        cell: ({ row }) => (
          <span className="text-sm text-slate-600">
            {row.original.date ? new Date(row.original.date).toLocaleDateString() : "—"}
          </span>
        ),
      },
      {
        accessorKey: "reference",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Reference" />,
        cell: ({ row }) => (
          <span className="font-mono text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded">
            {row.original.reference || "—"}
          </span>
        ),
      },
      {
        accessorKey: "sourceModule",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Source Module" />,
        cell: ({ row }) => (
          <span className="text-sm text-slate-600">{row.original.sourceModule || "—"}</span>
        ),
      },
      {
        accessorKey: "debit",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Debit" />,
        cell: ({ row }) => (
          <span className="text-sm font-semibold text-red-600 tabular-nums">
            {row.original.debit ? formatMoney(row.original.debit) : "—"}
          </span>
        ),
      },
      {
        accessorKey: "credit",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Credit" />,
        cell: ({ row }) => (
          <span className="text-sm font-semibold text-[#0A9F43] tabular-nums">
            {row.original.credit ? formatMoney(row.original.credit) : "—"}
          </span>
        ),
      },
      {
        accessorKey: "runningBalance",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Running Balance" />,
        cell: ({ row }) => (
          <span className="text-sm font-semibold text-slate-900 tabular-nums">
            {formatMoney(row.original.runningBalance)}
          </span>
        ),
      },
      {
        accessorKey: "user",
        header: ({ column }) => <DataTableColumnHeader column={column} title="User" />,
        cell: ({ row }) => (
          <span className="text-sm text-slate-600">{row.original.user || "—"}</span>
        ),
      },
      {
        accessorKey: "description",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Description" />,
        cell: ({ row }) => (
          <span className="text-sm text-slate-600 max-w-[200px] truncate block">
            {row.original.description || "—"}
          </span>
        ),
      },
    ],
    []
  );

  const journalColumns: ColumnDef<JournalEntry, any>[] = useMemo(
    () => [
      {
        accessorKey: "journalNumber",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Journal #" />,
        cell: ({ row }) => (
          <span className="font-mono text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded">
            {row.original.journalNumber || "—"}
          </span>
        ),
      },
      {
        accessorKey: "entryDate",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Date" />,
        cell: ({ row }) => (
          <span className="text-sm text-slate-600">
            {row.original.entryDate ? new Date(row.original.entryDate).toLocaleDateString() : "—"}
          </span>
        ),
      },
      {
        accessorKey: "narration",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Narration" />,
        cell: ({ row }) => (
          <span className="text-sm text-slate-600 max-w-[250px] truncate block">
            {row.original.narration || "—"}
          </span>
        ),
      },
      {
        accessorKey: "sourceModule",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Source" />,
        cell: ({ row }) => (
          <span className="text-sm text-slate-600">{row.original.sourceModule || "—"}</span>
        ),
      },
      {
        accessorKey: "debit",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Debit" />,
        cell: ({ row }) => (
          <span className="text-sm font-semibold text-red-600 tabular-nums">
            {row.original.debit ? formatMoney(row.original.debit) : "—"}
          </span>
        ),
      },
      {
        accessorKey: "credit",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Credit" />,
        cell: ({ row }) => (
          <span className="text-sm font-semibold text-[#0A9F43] tabular-nums">
            {row.original.credit ? formatMoney(row.original.credit) : "—"}
          </span>
        ),
      },
      {
        accessorKey: "createdBy",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Created By" />,
        cell: ({ row }) => (
          <span className="text-sm text-slate-600">{row.original.createdBy || "—"}</span>
        ),
      },
    ],
    []
  );

  const ledgerColumns: ColumnDef<LedgerEntry, any>[] = useMemo(
    () => [
      {
        accessorKey: "date",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Date" />,
        cell: ({ row }) => (
          <span className="text-sm text-slate-600">
            {row.original.date ? new Date(row.original.date).toLocaleDateString() : "—"}
          </span>
        ),
      },
      {
        accessorKey: "journalNumber",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Journal #" />,
        cell: ({ row }) => (
          <span className="font-mono text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded">
            {row.original.journalNumber || "—"}
          </span>
        ),
      },
      {
        accessorKey: "narration",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Narration" />,
        cell: ({ row }) => (
          <span className="text-sm text-slate-600 max-w-[250px] truncate block">
            {row.original.narration || "—"}
          </span>
        ),
      },
      {
        accessorKey: "source",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Source" />,
        cell: ({ row }) => (
          <span className="text-sm text-slate-600">{row.original.source || "—"}</span>
        ),
      },
      {
        accessorKey: "debit",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Debit" />,
        cell: ({ row }) => (
          <span className="text-sm font-semibold text-red-600 tabular-nums">
            {row.original.debit ? formatMoney(row.original.debit) : "—"}
          </span>
        ),
      },
      {
        accessorKey: "credit",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Credit" />,
        cell: ({ row }) => (
          <span className="text-sm font-semibold text-[#0A9F43] tabular-nums">
            {row.original.credit ? formatMoney(row.original.credit) : "—"}
          </span>
        ),
      },
      {
        accessorKey: "runningBalance",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Running Balance" />,
        cell: ({ row }) => (
          <span className="text-sm font-semibold text-slate-900 tabular-nums">
            {formatMoney(row.original.runningBalance)}
          </span>
        ),
      },
    ],
    []
  );

  if (accountError) {
    return (
      <PageTransition>
        <div className="flex min-h-[70vh] items-center justify-center p-6">
          <div className="text-center">
            <AlertCircle className="mx-auto mb-4 h-12 w-12 text-red-400" />
            <h2 className="mb-2 text-lg font-semibold text-gray-900">Failed to load account</h2>
            <p className="mb-4 text-sm text-gray-500">
              {(accountError as any)?.response?.data?.message || "Account not found."}
            </p>
            <Button onClick={() => navigate("/finance/accounts")} variant="outline" className="gap-2">
              <ArrowLeft className="h-4 w-4" /> Back to Accounts
            </Button>
          </div>
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <div className="min-h-screen bg-gray-50/50">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6">
          {/* Back button */}
          <button
            onClick={() => navigate("/finance/accounts")}
            className="flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-900 mb-4 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Accounts
          </button>

          {/* Header */}
          {accountLoading ? (
            <div className="mb-6 space-y-3">
              <Skeleton className="h-8 w-64" />
              <Skeleton className="h-5 w-96" />
              <div className="flex gap-4 mt-4">
                <Skeleton className="h-10 w-40" />
                <Skeleton className="h-10 w-32" />
                <Skeleton className="h-10 w-28" />
              </div>
            </div>
          ) : account ? (
            <>
              <div className="mb-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h1 className="text-2xl font-bold text-slate-900">
                      {account.accountName}
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">
                      {account.description || "Account details and financial history"}
                    </p>
                  </div>
                  <p className="text-3xl font-bold text-[#0A9F43] tabular-nums">
                    {formatMoney(account.balance)}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-3 mt-4">
                  <span className="font-mono text-xs bg-slate-100 text-slate-700 px-3 py-1 rounded-full">
                    {account.accountCode}
                  </span>
                  <Badge
                    variant="outline"
                    className={cn(
                      "text-xs font-medium border-0",
                      account.status === "Active"
                        ? "bg-emerald-50 text-emerald-700"
                        : "bg-slate-100 text-slate-500"
                    )}
                  >
                    {account.status}
                  </Badge>
                  <span className="text-xs text-slate-400">
                    {account.accountType}
                  </span>
                </div>
              </div>

              {/* Tabs */}
              <div className="bg-white border border-slate-200 rounded-xl shadow-sm mb-6">
                <div className="flex overflow-x-auto border-b border-slate-100">
                  {TABS.map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      className={cn(
                        "flex items-center gap-2 px-5 py-3.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap",
                        activeTab === tab.key
                          ? "border-[#0A9F43] text-[#0A9F43] bg-[#0A9F43]/5"
                          : "border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                      )}
                    >
                      {tab.icon}
                      {tab.label}
                    </button>
                  ))}
                </div>

                <div className="p-0">
                  {/* Overview Tab */}
                  {activeTab === "overview" && (
                    <div className="p-6">
                      <Card className="border border-slate-200 shadow-sm">
                        <CardHeader className="pb-3">
                          <h3 className="text-sm font-semibold text-slate-900">
                            Account Information
                          </h3>
                        </CardHeader>
                        <CardContent>
                          <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            <InfoItem label="Account Code" value={account.accountCode} />
                            <InfoItem label="Account Name" value={account.accountName} />
                            <InfoItem label="Account Type" value={account.accountType} />
                            <InfoItem label="Current Balance" value={formatMoney(account.balance)} highlight />
                            <InfoItem label="Status" value={account.status} />
                            <InfoItem label="Normal Balance" value={account.normalBalance || "—"} />
                            {account.isBankAccount !== undefined && (
                              <InfoItem label="Bank Account" value={account.isBankAccount ? "Yes" : "No"} />
                            )}
                            {account.bankName && (
                              <InfoItem label="Bank Name" value={account.bankName} />
                            )}
                            {account.accountNumber && (
                              <InfoItem label="Account Number" value={account.accountNumber} />
                            )}
                            {account.parentAccount && (
                              <InfoItem
                                label="Parent Account"
                                value={`${account.parentAccount.accountCode} - ${account.parentAccount.accountName}`}
                              />
                            )}
                            {account.description && (
                              <div className="sm:col-span-2 lg:col-span-3">
                                <InfoItem label="Description" value={account.description} />
                              </div>
                            )}
                          </dl>
                        </CardContent>
                      </Card>
                    </div>
                  )}

                  {/* Transactions Tab */}
                  {activeTab === "transactions" && (
                    <div className="p-2">
                      <DataTable
                        columns={txColumns}
                        data={transactions}
                        isLoading={txLoading}
                        isFetching={txFetching}
                        pageIndex={txPageIndex}
                        pageSize={txPageSize}
                        pageCount={txPagination.totalPages || 1}
                        setPagination={(updater) => {
                          const newState =
                            typeof updater === "function"
                              ? updater({ pageIndex: txPageIndex, pageSize: txPageSize })
                              : updater;
                          setTxPageIndex(newState.pageIndex);
                        }}
                        facets={[]}
                        showSearch={false}
                        emptyMessage="No transactions found"
                        emptyDescription="No transactions have been recorded for this account yet."
                        enableExport={false}
                        hidePagination={false}
                      />
                    </div>
                  )}

                  {/* Journal Entries Tab */}
                  {activeTab === "journals" && (
                    <div className="p-2">
                      <DataTable
                        columns={journalColumns}
                        data={journalEntries}
                        isLoading={journalLoading}
                        isFetching={journalFetching}
                        pageIndex={0}
                        pageSize={10}
                        pageCount={journalPagination.totalPages || 1}
                        facets={[]}
                        showSearch={false}
                        emptyMessage="No journal entries found"
                        emptyDescription="No journal entries are associated with this account."
                        enableExport={false}
                        hidePagination={false}
                      />
                    </div>
                  )}

                  {/* Ledger Tab */}
                  {activeTab === "ledger" && (
                    <div className="p-2">
                      <DataTable
                        columns={ledgerColumns}
                        data={ledgerEntries}
                        isLoading={ledgerLoading}
                        isFetching={ledgerFetching}
                        pageIndex={ledgerPageIndex}
                        pageSize={ledgerPageSize}
                        pageCount={ledgerPagination.totalPages || 1}
                        setPagination={(updater) => {
                          const newState =
                            typeof updater === "function"
                              ? updater({ pageIndex: ledgerPageIndex, pageSize: ledgerPageSize })
                              : updater;
                          setLedgerPageIndex(newState.pageIndex);
                        }}
                        facets={[]}
                        showSearch={false}
                        emptyMessage="No ledger entries found"
                        emptyDescription="No ledger entries exist for this account."
                        enableExport={false}
                        hidePagination={false}
                      />
                    </div>
                  )}

                  {/* Audit Trail Tab */}
                  {activeTab === "audit" && (
                    <div className="p-6">
                      <Card className="border border-slate-200 shadow-sm">
                        <CardHeader className="pb-3">
                          <h3 className="text-sm font-semibold text-slate-900">
                            Audit Trail
                          </h3>
                        </CardHeader>
                        <CardContent>
                          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <InfoItem
                              label="Created At"
                              value={
                                account.createdAt
                                  ? new Date(account.createdAt).toLocaleString()
                                  : "—"
                              }
                            />
                            <InfoItem
                              label="Last Updated"
                              value={
                                account.updatedAt
                                  ? new Date(account.updatedAt).toLocaleString()
                                  : "—"
                              }
                            />
                          </dl>
                        </CardContent>
                      </Card>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </PageTransition>
  );
}

function InfoItem({
  label,
  value,
  highlight = false,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div>
      <dt className="text-xs font-medium text-slate-500 mb-1">{label}</dt>
      <dd
        className={cn(
          "text-sm font-semibold",
          highlight ? "text-[#0A9F43]" : "text-slate-900"
        )}
      >
        {value}
      </dd>
    </div>
  );
}
